/**
 * Audio without Web Audio (W1-16 app-integration; ARCHITECTURE.md §8, §15).
 *
 *  - `hasWebAudio()`: the browser has an AudioContext constructor at all (probed before the real engine is built).
 *  - `createSilentAudioEngine(reason)`: an engine without sound whose transport runs on the page clock, so the
 *    preview and the timeline still play.
 *  - `resilientAudioEngine(primary, ...)`: the real engine creates its AudioContext lazily (first song, first play,
 *    first skin sound), so a context the browser refuses (blocked by policy, too many contexts, a sandbox) only shows
 *    up as AudioEngineError('unsupported') from one of those calls. The wrapper then swaps in the silent engine
 *    (position, rate, loop and offset carried over), tells the services once (`onSilent`, which shows the
 *    "Audio playback is not available" toast) and answers the failed call from the silent engine: a play still
 *    plays, a song load rejects with a FeatureUnavailableError. Its transport object never changes, so subscribers
 *    (the editor, the view-state persistence) keep receiving changes across the swap.
 */

import type { Sec } from '../core/time/types';
import type { Unsubscribe } from '../core/util/types';
import type { AudioEngine, MusicInfo, Transport, TransportState } from '../audio/types';
import { isAudioEngineError } from '../audio/errors';
import { defaultUnavailableError, type UnavailableErrorFactory } from './unavailable';

const MIN_RATE = 0.0625;
const MAX_RATE = 16;
const MIN_LOOP_SEC = 0.05;

function finite(x: number): number {
  return Number.isFinite(x) ? x : 0;
}

function perfNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

/** True when `scope` (default: the global object) has an AudioContext constructor (also the old WebKit one). */
export function hasWebAudio(scope: object = globalThis): boolean {
  const g = scope as { readonly AudioContext?: unknown; readonly webkitAudioContext?: unknown };
  return typeof g.AudioContext === 'function' || typeof g.webkitAudioContext === 'function';
}

/**
 * An audio engine without sound (Web Audio could not start): the transport runs on the page clock with every play
 * mode, rate, loop and return-to-start, so the preview and the timeline still play; loading songs, custom sounds
 * and calibration reject with a FeatureUnavailableError.
 */
export function createSilentAudioEngine(
  reason: string,
  now: () => number = perfNow,
  unavailableError: UnavailableErrorFactory = defaultUnavailableError,
): AudioEngine {
  let st: TransportState = { playing: false, mode: 'flow', rate: 1, startChartTime: 0, loop: null };
  /** Chart position at `baseMs` (while playing) or the paused position. */
  let baseChart = 0;
  let baseMs = now();
  let shiftSec = 0;
  const listeners = new Set<(s: TransportState) => void>();
  const emit = (): void => {
    for (const cb of [...listeners]) cb(st);
  };
  const position = (): number => {
    if (!st.playing) return baseChart;
    const t = baseChart + ((now() - baseMs) / 1000) * st.rate;
    const loop = st.loop;
    if (loop !== null && loop.end - loop.start >= MIN_LOOP_SEC && baseChart < loop.end && t >= loop.end) {
      return loop.start + ((t - loop.start) % (loop.end - loop.start));
    }
    return t;
  };
  const rebase = (t: number): void => {
    baseChart = t;
    baseMs = now();
  };
  const unavailable = (): Error => unavailableError('audio', reason);

  const transport: Transport = {
    get state() {
      return st;
    },
    play(opts = {}) {
      const from = opts.from ?? position();
      rebase(from);
      st = { ...st, playing: true, mode: opts.mode ?? 'flow', startChartTime: Math.max(0, from) };
      emit();
      return Promise.resolve();
    },
    stop(opts = {}) {
      const back = opts.returnToStart ?? st.mode === 'holdReturn';
      rebase(back ? st.startChartTime : Math.max(0, position()));
      st = { ...st, playing: false };
      emit();
    },
    seek(t: Sec) {
      rebase(finite(t));
      emit();
    },
    setRate(r) {
      if (!Number.isFinite(r) || r <= 0) return;
      rebase(position());
      st = { ...st, rate: Math.min(MAX_RATE, Math.max(MIN_RATE, r)) };
      emit();
    },
    setLoop(loop) {
      rebase(position());
      st = { ...st, loop };
      emit();
    },
    chartTime: () => Math.max(0, position()),
    audioChartTime: () => Math.max(0, position()),
    musicTime: () => position() + shiftSec,
    onChange(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };

  const music: MusicInfo | null = null;
  return {
    ensureContext: () => Promise.resolve(),
    context: null,
    loadMusic: () => Promise.reject(unavailable()),
    music,
    unloadMusic: () => undefined,
    musicChannels: () => null,
    transport,
    setOffset(offsetMs, infoOffsetSec, previewAudioDelayMs) {
      shiftSec = finite(offsetMs) / 1000 + finite(infoOffsetSec) + finite(previewAudioDelayMs) / 1000;
    },
    setEmissionSource: () => undefined,
    setHitsounds: () => undefined,
    setSkinSounds: () => Promise.resolve(),
    setPreservePitch: () => undefined,
    playHitsound: () => undefined,
    loadCustomHitsound: () => Promise.reject(unavailable()),
    setMetronome: () => undefined,
    setVolumes: () => undefined,
    setLatency: () => undefined,
    detectedLatencyMs: null,
    preview: () => undefined,
    calibrate: () => Promise.reject(unavailable()),
    dispose() {
      if (st.playing) transport.stop({ returnToStart: false });
      listeners.clear();
    },
  };
}

export interface ResilientAudioOptions {
  /** The text shown for the engine's 'unsupported' failure (the browser's reason, translated otherwise). */
  readonly describe: (err: unknown) => string;
  /** Web Audio turned out to be unusable; the engine runs silent from now on. Called once. */
  readonly onSilent: (reason: string) => void;
  /** The engine that takes over (default: `createSilentAudioEngine(reason)`). */
  readonly fallback?: (reason: string) => AudioEngine;
  /** Developer diagnostics. */
  readonly report?: (message: string, err?: unknown) => void;
}

/** An AudioEngine that becomes silent (instead of failing every call) when Web Audio cannot start. */
export interface ResilientAudioEngine extends AudioEngine {
  /** The silent engine took over. */
  readonly silent: boolean;
}

export function resilientAudioEngine(primary: AudioEngine, opts: ResilientAudioOptions): ResilientAudioEngine {
  let current: AudioEngine = primary;
  let silent = false;
  let offset: readonly [number, number, number] | null = null;
  const listeners = new Set<(s: TransportState) => void>();
  const forward = (s: TransportState): void => {
    for (const cb of [...listeners]) cb(s);
  };
  let offInner: Unsubscribe | null = primary.transport.onChange(forward);

  const swap = (err: unknown): void => {
    if (silent) return;
    silent = true;
    const reason = opts.describe(err);
    const old = primary.transport;
    let at: number;
    try {
      at = old.chartTime();
    } catch {
      at = 0;
    }
    const { rate, loop } = old.state;
    offInner?.();
    offInner = null;
    const next = (opts.fallback ?? ((r: string) => createSilentAudioEngine(r)))(reason);
    if (offset !== null) next.setOffset(offset[0], offset[1], offset[2]);
    next.transport.setRate(rate);
    next.transport.setLoop(loop);
    next.transport.seek(at);
    current = next;
    offInner = next.transport.onChange(forward);
    try {
      primary.dispose();
    } catch (e) {
      opts.report?.('the audio engine that could not start was not released', e);
    }
    opts.onSilent(reason);
    forward(next.transport.state);
  };

  /** Runs `run` on the engine in use; a first 'unsupported' failure of the real engine swaps and runs it again. */
  const guard = async <T>(run: (e: AudioEngine) => Promise<T>): Promise<T> => {
    const e = current;
    try {
      return await run(e);
    } catch (err) {
      if (e !== primary || !isAudioEngineError(err, 'unsupported')) throw err;
      swap(err);
      return run(current);
    }
  };

  const transport: Transport = {
    get state() {
      return current.transport.state;
    },
    play: (o) => guard((e) => e.transport.play(o)),
    stop(o) {
      current.transport.stop(o);
    },
    seek(t) {
      current.transport.seek(t);
    },
    setRate(r) {
      current.transport.setRate(r);
    },
    setLoop(loop) {
      current.transport.setLoop(loop);
    },
    chartTime: () => current.transport.chartTime(),
    audioChartTime: () => current.transport.audioChartTime(),
    musicTime: () => current.transport.musicTime(),
    onChange(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };

  return {
    get silent() {
      return silent;
    },
    ensureContext: () => guard((e) => e.ensureContext()),
    get context() {
      return current.context;
    },
    loadMusic: (asset, bytes) => guard((e) => e.loadMusic(asset, bytes)),
    get music() {
      return current.music;
    },
    unloadMusic() {
      current.unloadMusic();
    },
    musicChannels: () => current.musicChannels(),
    transport,
    setOffset(offsetMs, infoOffsetSec, previewAudioDelayMs) {
      offset = [offsetMs, infoOffsetSec, previewAudioDelayMs];
      current.setOffset(offsetMs, infoOffsetSec, previewAudioDelayMs);
    },
    setEmissionSource(src) {
      current.setEmissionSource(src);
    },
    setHitsounds(bank) {
      current.setHitsounds(bank);
    },
    setSkinSounds: (s) => guard((e) => e.setSkinSounds(s)),
    setPreservePitch(on) {
      current.setPreservePitch(on);
    },
    playHitsound(sound) {
      current.playHitsound(sound);
    },
    loadCustomHitsound: (asset, bytes) => guard((e) => e.loadCustomHitsound(asset, bytes)),
    setMetronome(on, beatTimes) {
      current.setMetronome(on, beatTimes);
    },
    setVolumes(v) {
      current.setVolumes(v);
    },
    setLatency(l) {
      current.setLatency(l);
    },
    get detectedLatencyMs() {
      return current.detectedLatencyMs;
    },
    preview(sound) {
      current.preview(sound);
    },
    calibrate: (onTick, taps, signal) =>
      guard((e) => (signal === undefined ? e.calibrate(onTick, taps) : e.calibrate(onTick, taps, signal))),
    dispose() {
      offInner?.();
      offInner = null;
      current.dispose();
      listeners.clear();
    },
  };
}
